/**
 * In-flight guards for async work started from the UI (OWA-1342).
 *
 * A flag held in component state cannot guard work that outlives the component: a
 * remount starts with a fresh flag while the first request is still running, and a
 * flag restored from the component cache stays set forever because the settle
 * that would clear it ran against the unmounted component. This registry lives at
 * module level, so a key stays in flight across remounts and is cleared when the
 * work settles, whether it succeeds, fails, or throws before its first await.
 */
import {useSyncExternalStore} from 'react';

const running = new Set<string>();
const listeners = new Set<() => void>();

function notify() {
    listeners.forEach(l => l());
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/**
 * True while work registered under `key` is running.
 */
export function isInFlight(key: string): boolean {
    return running.has(key);
}

/**
 * Run `work` unless work under the same key is already running. Returns `{started:
 * false}` without running it in that case. The key is released when the work
 * settles, and a rejection is passed on to the caller.
 */
export async function runInFlight<T>(key: string, work: () => T | Promise<T>): Promise<{started: true, result: T} | {started: false}> {
    if (running.has(key)) return {started: false};
    running.add(key);
    notify();
    try {
        return {started: true, result: await work()};
    }
    finally {
        running.delete(key);
        notify();
    }
}

/**
 * Re-renders the component when work under `key` starts or settles.
 */
export function useInFlight(key: string): boolean {
    return useSyncExternalStore(subscribe, () => running.has(key));
}
